import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { MapPin, Plus, RefreshCw, Loader2, Star, Search } from "lucide-react";
import { invalidateCitiesCache } from "@/components/CitySelect";

/**
 * SuperAdmin — Cities management.
 *
 * Powers Part 1 of the Cities Phase-1 rollout — a small admin table
 * that lets Super Admin add, edit, and soft-deactivate cities WITHOUT
 * a code deploy. The picker used everywhere else (`<CitySelect />`)
 * feeds off the exact same collection so every change here is visible
 * on vendor signup, the campaign wizard, and the public Explore feed
 * on the very next page load.
 *
 * Auth policy — list endpoint (`GET /admin/cities`) is gated at
 * ``require_admin`` so any admin can view. The mutation endpoints
 * are gated at ``require_superadmin`` so only true super-admins can
 * create/edit; the backend will 403 for a plain admin and we surface
 * the toast inline.
 */
export default function SuperAdminCitiesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({
    name: "",
    state: "",
    is_major: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.adminListCities();
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load cities.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (busy) return;
    const name = form.name.trim();
    const state = form.state.trim();
    if (!name || !state) {
      toast.error("City name and state are both required.");
      return;
    }
    setBusy(true);
    try {
      await api.superadminCreateCity({
        name, state,
        is_major: !!form.is_major,
        is_active: true,
      });
      toast.success(`Added ${name}`);
      setForm({ name: "", state: "", is_major: false });
      // Bust the picker cache so the new city is immediately selectable
      // on any tab without a hard refresh.
      invalidateCitiesCache();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add city.");
    } finally {
      setBusy(false);
    }
  };

  const onToggleActive = async (city, nextActive) => {
    try {
      await api.superadminUpdateCity(city.id, { is_active: nextActive });
      invalidateCitiesCache();
      toast.success(`${city.name} ${nextActive ? "activated" : "deactivated"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update city.");
    }
  };

  const onToggleMajor = async (city, nextMajor) => {
    try {
      await api.superadminUpdateCity(city.id, { is_major: nextMajor });
      invalidateCitiesCache();
      toast.success(`${city.name} ${nextMajor ? "pinned as Tier-1 metro" : "unpinned"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update city.");
    }
  };

  const filtered = items.filter((c) => {
    if (!q.trim()) return true;
    const needle = q.trim().toLowerCase();
    return (
      c.name.toLowerCase().includes(needle) ||
      (c.state || "").toLowerCase().includes(needle)
    );
  });

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6">
      <Toaster />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2">
            <MapPin className="h-6 w-6 text-[#F5A800]" /> Cities
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage the master list of cities used by vendor signup, campaign
            wizard, and the public Explore feed.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="cities-reload">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Reload
        </Button>
      </div>

      {/* Add-city form */}
      <Card className="p-5">
        <form onSubmit={onCreate} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto_auto] gap-3 items-end">
          <div className="space-y-1.5">
            <Label htmlFor="new-city-name">City name</Label>
            <Input
              id="new-city-name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Visakhapatnam"
              data-testid="cities-new-name"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-city-state">State</Label>
            <Input
              id="new-city-state"
              value={form.state}
              onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
              placeholder="e.g. Andhra Pradesh"
              data-testid="cities-new-state"
            />
          </div>
          <div className="flex items-center gap-2 pb-1">
            <Switch
              id="new-city-major"
              checked={form.is_major}
              onCheckedChange={(v) => setForm((f) => ({ ...f, is_major: v }))}
              data-testid="cities-new-major"
            />
            <Label htmlFor="new-city-major" className="cursor-pointer whitespace-nowrap">
              Tier-1 metro
            </Label>
          </div>
          <Button type="submit" disabled={busy} data-testid="cities-new-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            Add city
          </Button>
        </form>
      </Card>

      {/* Search + list */}
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by city or state…"
          className="pl-9"
          data-testid="cities-search"
        />
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-4 space-y-2">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground text-sm">
            {q ? "No cities match your search." : "No cities yet — add one above."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b">
                <tr className="text-left">
                  <th className="p-3 font-semibold">City</th>
                  <th className="p-3 font-semibold">State</th>
                  <th className="p-3 font-semibold whitespace-nowrap">Tier-1</th>
                  <th className="p-3 font-semibold whitespace-nowrap">Active</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr
                    key={c.id}
                    className="border-b last:border-0 hover:bg-muted/20"
                    data-testid={`cities-row-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <td className="p-3 font-medium">
                      <span className="inline-flex items-center gap-2">
                        {c.name}
                        {c.is_major && (
                          <Badge className="bg-[#F5A800] text-black hover:bg-[#F5A800]">
                            <Star className="h-3 w-3 mr-1" /> Metro
                          </Badge>
                        )}
                        {!c.is_active && (
                          <Badge variant="outline" className="text-muted-foreground">
                            Inactive
                          </Badge>
                        )}
                      </span>
                    </td>
                    <td className="p-3 text-muted-foreground">{c.state}</td>
                    <td className="p-3">
                      <Switch
                        checked={!!c.is_major}
                        onCheckedChange={(v) => onToggleMajor(c, v)}
                        aria-label={`Toggle Tier-1 metro for ${c.name}`}
                      />
                    </td>
                    <td className="p-3">
                      <Switch
                        checked={!!c.is_active}
                        onCheckedChange={(v) => onToggleActive(c, v)}
                        aria-label={`Toggle active for ${c.name}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
